import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Flame, Wind, RotateCcw, MessageSquareWarning, CheckCircle2 } from "lucide-react";

const INDUSTRIES = {
  healthcare: {
    label: "Healthcare",
    who: "Nurses · techs · aides · clinicians",
    h1: ["For the ones who clock in", "scared and stay anyway."],
    sub: "Twelve-hour floors. Outcomes you carry home. A break room that's really a hallway. T.A.C.C. gives your clinical staff a 90-second practice that fits between rooms.",
    seoTitle: "Workplace Wellness for Healthcare Workers & Nurses | T.A.C.C.",
    seoDesc: "T.A.C.C. is a pre-shift mental resilience program for nurses, techs and clinicians — narrated audio modules, daily grounding and micro-recovery built for 12-hour floors.",
    pressures: [
      "Twelve-hour shifts with no real recovery window",
      "Carrying patient outcomes home after clock-out",
      "Compassion fatigue that starts to look like numbness",
      "Short-staffed floors where breaks disappear first",
    ],
    fits: [
      { icon: Flame, m: "Module 02 · Accepting Where You Are", t: "Wherever the shift puts them, they start from where they actually are — no bracing, no blame — before badge-in." },
      { icon: RotateCcw, m: "Module 05 · The Decision", t: "The turning point between rooms: stop running from what the last patient stirred up, and decide to go through it." },
      { icon: Wind, m: "Module 04 · The Wall of Adversity", t: "The loss, the fear, the weight only they know about — faced and walked through before the hard family conversation, not after." },
      { icon: MessageSquareWarning, m: "Module 07 · Wisdom — Processing Your Pain", t: "Charge-desk friction moved from raw emotion to wisdom — so it stops riding home in the car." },
    ],
  },
  "first-responders": {
    label: "First Responders",
    who: "EMS · fire · dispatch · law enforcement",
    h1: ["For the ones who", "run toward it."],
    sub: "Tones drop mid-meal. Scenes don't unsee themselves. The drive home is the only debrief most crews get. T.A.C.C. turns that drive into a protocol.",
    seoTitle: "Mental Resilience Program for First Responders & EMS | T.A.C.C.",
    seoDesc: "T.A.C.C. gives EMS, fire, dispatch and law enforcement a stoic, practical resilience practice — pre-shift grounding, breath protocols and micro-recovery between calls.",
    pressures: [
      "Calls that stack with no space in between",
      "Scenes that follow the crew home",
      "A culture where asking for help reads as weakness",
      "The quiet drive home with no debrief",
    ],
    fits: [
      { icon: Flame, m: "Module 01 · Understanding God", t: "The foundation before the first tone — a crew that knows it was never carrying the call alone." },
      { icon: Wind, m: "Module 04 · The Wall of Adversity", t: "Scenes that don't unsee themselves — faced and walked through, not stacked up until they break the wall." },
      { icon: RotateCcw, m: "Module 06 · Knowledge — Facing Your Past", t: "The what, when, where and why of the calls that follow them home — named honestly in the cab, the bay, the drive back." },
      { icon: MessageSquareWarning, m: "Module 02 · Accepting Where You Are", t: "Take responsibility for what's theirs and set down what isn't — so the next call starts clean." },
    ],
  },
  hospitality: {
    label: "Hospitality & Service",
    who: "Hotels · restaurants · retail · service floors",
    h1: ["For the ones who smile", "through the double."],
    sub: "Rude guests, clopens, tip anxiety, and being treated like furniture. T.A.C.C. gives service teams a way to stop carrying strangers' moods home.",
    seoTitle: "Employee Wellness for Hospitality, Retail & Service Teams | T.A.C.C.",
    seoDesc: "T.A.C.C. helps hotel, restaurant and retail teams handle rude customers, doubles and clopens — narrated resilience modules and 90-second resets built for the floor.",
    pressures: [
      "Guests who are cruel on the wrong day",
      "Doubles and clopens with no recovery in between",
      "Smiling as a job requirement, whatever is underneath",
      "Being invisible until something goes wrong",
    ],
    fits: [
      { icon: MessageSquareWarning, m: "Module 07 · Wisdom — Processing Your Pain", t: "Works on difficult guests too — see the exchange clearly and decide what you carry home from table twelve." },
      { icon: RotateCcw, m: "Module 05 · The Decision", t: "The turning point in a walk-in cooler or a stockroom: stop running from the dread and go through it." },
      { icon: Flame, m: "Module 03 · Embracing Your Journey", t: "The clopen has a place in their story — embrace the whole of it, the good and the bad, before the alarm rings." },
      { icon: Wind, m: "Module 02 · Accepting Where You Are", t: "Accept the double you're actually working, not the story about it — and find the power acceptance gives you." },
    ],
  },
  "call-centers": {
    label: "Call Centers",
    who: "Support desks · dispatch · contact centers",
    h1: ["For the ones on call", "forty times a day."],
    sub: "Back-to-back queues. Anger through the headset. Empathy that gets QA-scored. T.A.C.C. fits in the seconds between calls — because that's all your team gets.",
    seoTitle: "Burnout Prevention for Call Center & Support Teams | T.A.C.C.",
    seoDesc: "T.A.C.C. gives contact center and support teams micro-recovery between calls, pre-shift grounding and breath protocols — practical resilience for queue work.",
    pressures: [
      "Queues that never show the bottom",
      "Absorbing anger through a headset for hours",
      "Empathy graded on a scorecard",
      "No space between one hard call and the next",
    ],
    fits: [
      { icon: RotateCcw, m: "Module 05 · The Decision", t: "After-call work becomes a decision point: stop running from the escalation and go through it before the next hello." },
      { icon: Wind, m: "Module 04 · The Wall of Adversity", t: "The escalation that gets under the skin — faced and walked through between calls, not carried into the next one." },
      { icon: MessageSquareWarning, m: "Module 07 · Wisdom — Processing Your Pain", t: "The caller cannot be fixed. What your agent carries home can be processed into wisdom instead of resentment." },
      { icon: Flame, m: "Module 08 · Understanding — Becoming the Healer", t: "It was never random pain — it was preparation. Agents who become healers for the person in the next seat, in month six, not just week one." },
    ],
  },
};

const ROLLOUT = [
  { n: "01", t: "Pick a tier", d: "Corporate Starter or Team. Flat pricing, no per-seat games. Your people get access the same week." },
  { n: "02", t: "Your team gets the doctrine", d: "All 8 narrated audio modules, the printable Companion Manual, daily grounding check-ins with streaks, and the T.A.C.C. coach." },
  { n: "03", t: "We watch direction, not people", d: "Participation, daily check-ins, module completion, self-reported readiness. Signals of change — never surveillance." },
];

export default function IndustryPage() {
  const { slug } = useParams();
  const data = INDUSTRIES[slug];

  useEffect(() => {
    if (!data) return;
    document.title = data.seoTitle;
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
    }
    m.setAttribute("content", data.seoDesc);
    window.scrollTo(0, 0);
  }, [data]);

  if (!data) return <Navigate to="/b2b" replace />;

  return (
    <main className="min-h-screen pt-32 md:pt-40 pb-28" data-testid={`industry-page-${slug}`}>
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="section-tag mb-4">§ For {data.label} · {data.who}</div>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.65, 0, 0.35, 1] }}
          className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight mb-6 max-w-4xl"
        >
          {data.h1[0]} <span className="italic text-[#B45309]">{data.h1[1]}</span>
        </motion.h1>
        <p className="text-[#44403C] text-lg md:text-xl leading-relaxed max-w-2xl mb-16">{data.sub}</p>

        <div className="section-tag mb-6">§ The weight this floor carries</div>
        <div className="grid grid-cols-12 gap-4 mb-20" data-testid="industry-pressures">
          {data.pressures.map((p, i) => (
            <div key={p} className="tacc-card col-span-12 md:col-span-6 p-6 flex items-start gap-4">
              <span className="font-display text-2xl text-[#B45309] leading-none shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <p className="text-[#44403C] text-sm leading-relaxed">{p}</p>
            </div>
          ))}
        </div>

        <div className="section-tag mb-6">§ What T.A.C.C. gives them</div>
        <div className="grid grid-cols-12 gap-4 mb-20" data-testid="industry-fits">
          {data.fits.map((f) => (
            <div key={f.m} className="tacc-card col-span-12 md:col-span-6 p-8">
              <f.icon size={22} className="text-[#B45309] mb-4" strokeWidth={1.4} />
              <h3 className="font-heading text-lg tracking-tight mb-2">{f.m}</h3>
              <p className="text-[#78716C] text-sm leading-relaxed">{f.t}</p>
            </div>
          ))}
        </div>

        <div className="section-tag mb-6">§ What a rollout looks like</div>
        <div className="grid grid-cols-12 gap-4 mb-12" data-testid="industry-rollout">
          {ROLLOUT.map((r) => (
            <div key={r.n} className="tacc-card col-span-12 md:col-span-4 p-8">
              <span className="font-display text-4xl text-[#44403C] leading-none block mb-4">{r.n}</span>
              <h3 className="font-heading text-lg tracking-tight mb-2">{r.t}</h3>
              <p className="text-[#78716C] text-sm leading-relaxed">{r.d}</p>
            </div>
          ))}
        </div>

        <div className="tacc-card p-6 md:p-8 mb-16 flex items-start gap-4" data-testid="industry-honesty">
          <CheckCircle2 size={20} className="text-[#B45309] mt-0.5 shrink-0" strokeWidth={1.5} />
          <p className="text-[#78716C] text-sm leading-relaxed max-w-3xl">
            We don't publish invented percentages or borrowed testimonials. We measure direction of change —
            participation, daily grounding, completion, and what your people tell us in their own words.
            When your team has a story, it will be a real one.
          </p>
        </div>

        <div className="flex flex-wrap gap-4">
          <Link to="/b2b#quote" data-testid="industry-quote-cta" className="btn-ember text-sm">
            Request a quote <ArrowUpRight size={16} />
          </Link>
          <Link to="/modules" data-testid="industry-modules-cta" className="btn-outline text-sm">
            Hear the modules first
          </Link>
        </div>
      </div>
    </main>
  );
}
